// @flow

import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import { useTableManageColumns } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useUniqueTableName from '../../../hooks/useUniqueTableName';
import useBaseTableProps from '../../../hooks/useBaseTableProps';
import { HEADERS } from './constants';
import ApprovedAmountCell from '../ApprovedAmountCell';
import { VARIANCE_CELL_SX, NUMBER_FORMAT_OPTIONS } from '../constants';
import useIntl from '../../../hooks/useIntl';
import { getHudVoucherAdjustments } from '../../../services/hudVouchersService';
import { getHudVoucherAdjustmentsQueryKey } from '../hooks.hudVoucherQueryKeys';
import messages from './messages';
import { certificationTypeToHudVoucherTableLabel } from '../../../utils/certificationTypeLabel';
import type {
  HudVoucherAdjustmentApi,
  HudVoucherAdjustmentTableMappedScalars,
  HudVoucherAdjustmentTableRow,
} from './types';
import { HUD_VOUCHER_ADJUSTMENT_MAPPED_SCALARS_ALL_NULL } from './types';
import { canEditApprovedAmount } from '../utils';

const BLOCK_SPACER_DIV_CELL = { variant: 'div', value: '' };
const BLOCK_SPACER_ROW_SX = { minHeight: 12, pointerEvents: 'none' };
// A unit change inside one block is a section break, not a new block: hairline, no gap.
const UNIT_DIVIDER_ROW_SX = {
  minHeight: 1,
  pointerEvents: 'none',
  borderTop: '1px dashed',
  borderTopColor: 'divider',
};
const SPACER_CELLS: { [string]: typeof BLOCK_SPACER_DIV_CELL } =
  Object.fromEntries(HEADERS.map(({ id }) => [id, BLOCK_SPACER_DIV_CELL]));
/** Maps voucher-level submission status to the label used in the Adjustments table. */
export function mapVoucherSubmissionStatusToRowStatus(
  submissionStatus: string | void,
): string {
  if (submissionStatus === 'Draft') {
    return 'Not Submitted';
  }
  if (submissionStatus === 'Submitted') {
    return 'Submitted';
  }
  if (submissionStatus === 'Paid' || submissionStatus === 'Approved') {
    return 'Successful';
  }
  return submissionStatus || 'Not Submitted';
}

/**
 * Coerce numeric adjustment fields that may arrive as strings from the API
 * (pg `DECIMAL` / integer columns) into finite numbers.
 */
function toNumber(value: number | string | null | void): number | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function mapRenamedFieldsFromAdjustment(
  raw: HudVoucherAdjustmentApi,
): HudVoucherAdjustmentTableMappedScalars {
  return {
    effectiveDate: raw.effectiveDate,
    assistancePayment: toNumber(raw.assistancePayment),
    approvedAmount: toNumber(raw.approvedAmount),
    requested: toNumber(raw.requestedAmount),
    adjustmentBeginDate: raw.beginningDate,
    adjustmentEndDate: raw.endingDate,
    daysBeginningPartialMonth: toNumber(raw.beginningPartialMonthNumberOfDays),
    dailyRateBeginningPartialMonth: toNumber(
      raw.beginningPartialMonthDailyRate,
    ),
    fullMonths: toNumber(raw.fullMonthNumberOfMonths),
    fullMonthlyRate: toNumber(raw.fullMonthMonthlyRate),
    daysEndingPartialMonth: toNumber(raw.endingPartialMonthNumberOfDays),
    dailyRateEndingPartialMonth: toNumber(raw.endingPartialMonthDailyRate),
    calculationDetailsAmount: toNumber(raw.amount),
  };
}

/** Maps one API / DB row into the shape expected by `useAdjustmentsTable` / `useRows`. */
export function mapHudVoucherAdjustmentToTableRow(
  raw: HudVoucherAdjustmentApi,
  voucherSubmissionStatus: string | void,
): HudVoucherAdjustmentTableRow {
  const snap =
    raw.householdSnapshot && typeof raw.householdSnapshot === 'object'
      ? raw.householdSnapshot
      : {};
  const first = String(snap.headOfHouseholdFirstName || '').trim();
  const last = String(snap.headOfHouseholdLastName || '').trim();
  const initial = snap.headOfHouseholdInitial;
  const initialPart =
    initial !== null && initial !== undefined && String(initial).trim() !== ''
      ? `${String(initial).trim()}.`
      : '';
  const nameParts = [first, initialPart, last].filter((p) => p !== '');
  const headOfHousehold =
    nameParts.length > 0 ? nameParts.join(' ').trim() : '—';

  const billingType = raw.billingType;
  let priorOrNew = '—';
  if (billingType === 'prior_billing') {
    priorOrNew = 'Prior';
  }
  if (billingType === 'new_billing') {
    priorOrNew = 'New';
  }

  const isNew = raw.isNewCertification === true;
  const newCert = isNew ? 'Yes' : 'No';

  return {
    ...mapRenamedFieldsFromAdjustment(raw),
    id: raw.id,
    adjustmentBlockId:
      raw.adjustmentBlockId != null &&
      String(raw.adjustmentBlockId).trim() !== ''
        ? String(raw.adjustmentBlockId).trim()
        : null,
    status: mapVoucherSubmissionStatusToRowStatus(voucherSubmissionStatus),
    headOfHousehold,
    // Per-row unit first: a transfer block spans two units, and the block-level snapshot carries
    // only one. Falls back to the snapshot for rows written before the UT-O/UT-I split.
    unit: String(raw.unitNumber || snap.unitNumber || '').trim() || '—',
    hudUnitNumber: String(raw.unitNumber || snap.unitNumber || '').trim(),
    priorOrNew,
    newCert,
    certType: certificationTypeToHudVoucherTableLabel(
      raw.certificationType,
      raw.transferDirection,
      raw.moveOutCode,
    ),
  };
}

function blockSpacerDataRow(
  n: number,
  isUnitDivider?: boolean = false,
): HudVoucherAdjustmentTableRow {
  return {
    ...HUD_VOUCHER_ADJUSTMENT_MAPPED_SCALARS_ALL_NULL,
    id: `adjustment-${isUnitDivider ? 'unit-divider' : 'block-spacer'}-${String(
      n,
    )}`,
    adjustmentBlockId: null,
    isBlockSpacer: true,
    isUnitDivider,
    status: '',
    headOfHousehold: '',
    unit: '',
    hudUnitNumber: '',
    priorOrNew: '',
    newCert: '',
    certType: '',
  };
}

export function insertAdjustmentBlockSpacerRows(
  rows: Array<HudVoucherAdjustmentTableRow>,
): Array<HudVoucherAdjustmentTableRow> {
  if (rows.length < 2) {
    return rows;
  }
  const out: Array<HudVoucherAdjustmentTableRow> = [];
  let n = 0;
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    // A block is one household's adjustment. Units are grouped INSIDE it, and because the workbook
    // prints billing-major (all prior, then all new) a unit's rows are not contiguous — so a unit
    // change is a section break within the block, never a block boundary.
    const previous = i > 0 ? rows[i - 1] : null;
    const blockChanged =
      previous != null && previous.adjustmentBlockId !== row.adjustmentBlockId;
    const unitChanged =
      previous != null && !blockChanged && previous.unit !== row.unit;
    if (blockChanged || unitChanged) {
      n += 1;
      out.push(blockSpacerDataRow(n, unitChanged));
    }
    out.push(row);
  }
  return out;
}

export function useHudVoucherAdjustmentsData({
  show,
  organizationId,
  propertyId,
  voucherId,
  voucherSubmissionStatus,
}: {
  show: boolean,
  organizationId?: string,
  propertyId?: string,
  voucherId?: string,
  voucherSubmissionStatus?: string,
}): {|
  adjustments: Array<HudVoucherAdjustmentTableRow>,
  isLoading: boolean,
|} {
  const intl = useIntl();

  const adjustmentsQueryKey = useMemo(
    () =>
      getHudVoucherAdjustmentsQueryKey(organizationId, propertyId, voucherId),
    [organizationId, propertyId, voucherId],
  );

  const isQueryEnabled = Boolean(
    show && organizationId && propertyId && voucherId,
  );

  const {
    data: adjustmentsRaw = ([]: Array<HudVoucherAdjustmentApi>),
    isLoading,
  } = useQuery(
    adjustmentsQueryKey,
    () =>
      getHudVoucherAdjustments({
        organizationId: organizationId || '',
        propertyId: propertyId || '',
        voucherId: voucherId || '',
      }),
    {
      enabled: isQueryEnabled,
      onError: () => {
        toastr.error('Error', intl.formatMessage(messages.loadError));
      },
    },
  );

  const adjustments = useMemo(() => {
    const list = Array.isArray(adjustmentsRaw) ? adjustmentsRaw : [];
    return list.map((row) =>
      mapHudVoucherAdjustmentToTableRow(row, voucherSubmissionStatus),
    );
  }, [adjustmentsRaw, voucherSubmissionStatus]);

  return { adjustments, isLoading };
}

function useRows({
  adjustments,
  hasVariance,
  isReadOnly,
  onSaveApprovedAmount,
  canManage,
}) {
  return useMemo(() => {
    return adjustments.map((row) => {
      if (row.isBlockSpacer === true) {
        return {
          ...SPACER_CELLS,
          id: row.id,
          sx:
            row.isUnitDivider === true
              ? UNIT_DIVIDER_ROW_SX
              : BLOCK_SPACER_ROW_SX,
        };
      }

      return {
        id: row.id,
        headOfHousehold: {
          variant: 'link',
          to: '#',
          value: row.headOfHousehold,
        },
        unit: {
          variant: 'link',
          to: '#',
          value: row.unit,
        },
        hudUnitNumber: {
          value: row.hudUnitNumber,
        },
        priorOrNew: {
          value: row.priorOrNew,
        },
        newCert: {
          value: row.newCert,
        },
        certType: {
          value: row.certType,
        },
        effectiveDate: {
          variant: 'date',
          value: row.effectiveDate,
        },
        assistancePayment: {
          variant: 'currency',
          value: row.assistancePayment,
          numberFormatOptions: NUMBER_FORMAT_OPTIONS,
        },
        requested: {
          variant: 'currency',
          value: row.requested,
          numberFormatOptions: NUMBER_FORMAT_OPTIONS,
        },
        approvedAmount:
          canEditApprovedAmount(hasVariance, isReadOnly, canManage) &&
          row.requested != null
            ? {
                sx: VARIANCE_CELL_SX,
                value: (
                  <ApprovedAmountCell
                    key={row.id}
                    initialValue={row.approvedAmount}
                    onSave={(amount) => onSaveApprovedAmount?.(row, amount)}
                  />
                ),
              }
            : {
                variant: 'currency',
                value: row.approvedAmount,
                numberFormatOptions: NUMBER_FORMAT_OPTIONS,
              },
        adjustmentBeginDate: {
          variant: 'date',
          value: row.adjustmentBeginDate,
        },
        adjustmentEndDate: {
          variant: 'date',
          value: row.adjustmentEndDate,
        },
        daysBeginningPartialMonth: {
          variant: 'number',
          value: row.daysBeginningPartialMonth,
        },
        dailyRateBeginningPartialMonth: {
          variant: 'currency',
          value: row.dailyRateBeginningPartialMonth,
          numberFormatOptions: NUMBER_FORMAT_OPTIONS,
        },
        fullMonths: {
          variant: 'number',
          value: row.fullMonths,
        },
        fullMonthlyRate: {
          variant: 'currency',
          value: row.fullMonthlyRate,
          numberFormatOptions: NUMBER_FORMAT_OPTIONS,
        },
        daysEndingPartialMonth: {
          variant: 'number',
          value: row.daysEndingPartialMonth,
        },
        dailyRateEndingPartialMonth: {
          variant: 'currency',
          value: row.dailyRateEndingPartialMonth,
          numberFormatOptions: NUMBER_FORMAT_OPTIONS,
        },
        calculationDetailsAmount: {
          variant: 'currency',
          value: row.calculationDetailsAmount,
          numberFormatOptions: NUMBER_FORMAT_OPTIONS,
        },
      };
    });
  }, [adjustments, hasVariance, isReadOnly, onSaveApprovedAmount, canManage]);
}

export function useAdjustmentsTable(props?: {
  adjustments?: Array<HudVoucherAdjustmentTableRow>,
  isLoading?: boolean,
  hasVariance?: boolean,
  isReadOnly?: boolean,
  onSaveApprovedAmount?: (
    row: HudVoucherAdjustmentTableRow,
    amount: number,
  ) => void,
  canManage: boolean,
}): {|
  baseTableProps: Object,
  name: string,
  allColumnsHidden: boolean,
  columnOptions: mixed,
  filteredHeaders: mixed,
  selectedColumns: mixed,
  handleColumnChange: mixed,
  isLoading: boolean,
  rows: Array<Object>,
  count: number,
  totalCount: number,
|} {
  const {
    adjustments = ([]: Array<HudVoucherAdjustmentTableRow>),
    isLoading = false,
    hasVariance = false,
    isReadOnly = false,
    onSaveApprovedAmount,
    canManage,
  } = props ?? {};
  const name = useUniqueTableName('adjustments-table');
  const baseTableProps = useBaseTableProps();

  const adjustmentsWithBlockSpacers = useMemo(
    () => insertAdjustmentBlockSpacerRows(adjustments),
    [adjustments],
  );

  const rows = useRows({
    adjustments: adjustmentsWithBlockSpacers,
    hasVariance,
    isReadOnly,
    onSaveApprovedAmount,
    canManage,
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers: HEADERS,
    firstRow: rows[0],
  });

  return {
    baseTableProps,
    name,
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    isLoading,
    rows,
    count: adjustments.length,
    totalCount: adjustments.length,
  };
}
